import { useRef, useState } from "react";
import { Button, Alert, CircularProgress, Snackbar } from "@mui/material";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import { uploadDocument } from "../../../../services/documentsService";

const ALLOWED_EXTENSIONS = ["pdf", "pptx", "ppt", "xlsx", "eml", "txt", "docx", "doc"];

const ALLOWED_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation", // .pptx
  "application/vnd.ms-powerpoint",                                              // .ppt
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",         // .xlsx
  "message/rfc822",                                                             // .eml
  "text/plain",                                                                 // .txt
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",    // .docx
  "application/msword",                                                          // .doc
];

const MAX_SIZE_MB = 6;

const UPLOAD_SUCCESS_MESSAGE = (
  <>
    <strong>File Uploaded Successfully</strong>
    <br />
    It may take up to 24 hours to reflect in the portal
  </>
);

export default function UploadArea({ documents, companyName, partnerCode, dealId, documentType, onUploadSuccess, onAdd, onDelete, onProgress, onCancel }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [successSnackbarOpen, setSuccessSnackbarOpen] = useState(false);
  const [errorSnackbarOpen, setErrorSnackbarOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    fileInputRef.current.value = "";

    const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
    const typeAllowed = ALLOWED_TYPES.includes(file.type) || ALLOWED_EXTENSIONS.includes(ext);
    if (!typeAllowed) {
      setErrorMessage(`"${file.name}" is not supported. Allowed: PDF, PPT, PPTX, XLSX, EML, TXT.`);
      setErrorSnackbarOpen(true);
      return;
    }
    if (file.size === 0) {
      setErrorMessage("File is empty and cannot be uploaded.");
      setErrorSnackbarOpen(true);
      return;
    }
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      const sizeMB = (file.size / 1024 / 1024).toFixed(1);
      setErrorMessage(`"${file.name}" is ${sizeMB}MB. Maximum allowed size is ${MAX_SIZE_MB}MB.`);
      setErrorSnackbarOpen(true);
      return;
    }
    const isDuplicate = documents?.some((d) => d.name === file.name);
    if (isDuplicate) {
      setErrorMessage(`"${file.name}" has already been uploaded.`);
      setErrorSnackbarOpen(true);
      return;
    }

    if (!companyName?.trim() || (documentType !== "ir" && documentType !== "boarddeck")) {
      setErrorMessage("Company and document type are required for upload.");
      setErrorSnackbarOpen(true);
      return;
    }

    setUploading(true);
    try {
      const result = await uploadDocument(partnerCode, dealId, documentType, file);
      if (result.success) {
        setSuccessSnackbarOpen(true);
        // Add the uploaded document to the list immediately
        onAdd({ name: file.name, path: result.path }, documentType);
      } else {
        setErrorMessage("Upload failed: " + (result.message || "Unknown error"));
        setErrorSnackbarOpen(true);
      }
    } catch (err) {
      setErrorMessage(err?.message ?? "Upload failed.");
      setErrorSnackbarOpen(true);
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.ppt,.pptx,.xlsx,.eml,.txt, .docx, .doc"
          style={{ display: "none" }}
          onChange={handleFileChange}
        />

        <Button
          size="small"
          variant="outlined"
          disabled={uploading || !documentType}
          startIcon={uploading ? <CircularProgress size={16} /> : <UploadFileOutlinedIcon />}
          onClick={() => {
            fileInputRef.current?.click();
          }}
          sx={{ textTransform: "none", color: "black", border: "1px solid lightgray", borderRadius: "5px" }}
        >
          {uploading ? "Uploading…" : "Upload File"}
        </Button>

      {/* ── Success toast (green) ── */}
      <Snackbar
        open={successSnackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSuccessSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSuccessSnackbarOpen(false)}
          severity="success"
          variant="filled"
          sx={{ width: "100%", "& .MuiAlert-message": { whiteSpace: "pre-line" } }}
        >
          {UPLOAD_SUCCESS_MESSAGE}
        </Alert>
      </Snackbar>

      {/* ── Error toast (red) ── */}
      <Snackbar
        open={errorSnackbarOpen}
        autoHideDuration={6000}
        onClose={() => setErrorSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setErrorSnackbarOpen(false)}
          severity="error"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
}